import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Slider from '@mui/material/Slider';
import FormHelperText from '@mui/material/FormHelperText';

export default function EmbeddingParametersPanel({
  embeddingParamsOpen,
  setEmbeddingParamsOpen,
  embeddingDimensions,
  onEmbeddingDimensionsChange,
  EMBEDDING_DIM_OPTIONS
}) {
  return (
    <Box
      mb={.5}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        border: '1px solid',
        borderColor: 'grey.300',
        backgroundColor: 'rgba(255,255,255,0.85)',
        borderRadius: 2,
        padding: 1.5,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600}>
            Embedding Parameters
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1, fontWeight: 400, fontSize: '0.75em' }}
          >
            Dimensions: <b>{embeddingDimensions}</b>
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Tooltip
            title="Select the number of dimensions for the embedding vectors. Higher dimensions will be reduced to 3D for visualization using PCA."
            placement="top"
            arrow
          >
            <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'pointer', mr: 1 }} />
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => setEmbeddingParamsOpen((open) => !open)}
            aria-label={embeddingParamsOpen ? "Collapse" : "Expand"}
            sx={{
              transform: embeddingParamsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={embeddingParamsOpen}>
        {embeddingParamsOpen && (
          <>
            <Box>
              <Tooltip
                title="Choose the dimensionality of the embeddings to use. If greater than 3, PCA will be applied for 3D visualization."
                placement="top"
                arrow
              >
                <Typography variant="body2" fontWeight={500} mb={0.5}>
                  Embedding Dimensions: {embeddingDimensions}
                </Typography>
              </Tooltip>
              <Slider
                min={0}
                max={EMBEDDING_DIM_OPTIONS.length - 1}
                step={1}
                value={EMBEDDING_DIM_OPTIONS.indexOf(embeddingDimensions)}
                marks={EMBEDDING_DIM_OPTIONS.map((val, idx) => ({
                  value: idx,
                  label: val.toString(),
                }))}
                onChange={(_, v) => onEmbeddingDimensionsChange(EMBEDDING_DIM_OPTIONS[v])}
                valueLabelDisplay="auto"
                sx={{
                  width: '95%',
                  '& .MuiSlider-markLabel': {
                    fontSize: '0.6rem',
                    mt: 0.125,
                  },
                }}
                valueLabelFormat={(idx) => EMBEDDING_DIM_OPTIONS[idx]}
              />
              <FormHelperText sx={{ mt: 0.25 }}>
                {embeddingDimensions > 3
                  ? 'Using PCA to reduce to 3D for visualization'
                  : 'Direct 3D visualization'}
              </FormHelperText>
            </Box>
          </>
        )}
      </Collapse>
    </Box>
  );
}
